import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { sortBy, sumBy } from 'lodash';
import groupBy from 'lodash/groupBy';
import React from 'react';
import { TreeNodeData } from '@mantine/core';
import { Box } from 'src/design/atoms/Box';
import { Grid } from 'src/design/atoms/Grid';
import { Text } from 'src/design/atoms/Text';
import { Tree } from 'src/design/molecules/Tree';
import { JobPoDataDto } from 'src/api/FiretrolApiClient';
import { formatCurrency } from 'src/design/atoms/Currency';

interface POTabProps {
  material: { [key: string]: JobPoDataDto[] };
}

export const POTab: React.FC<POTabProps> = ({ material }) => {
  const treeData = React.useMemo((): TreeNodeData[] => {
    let totalRemFinal = 0;
    let totalCost = 0;
    const nodes: TreeNodeData[] = [];

    sortBy(Object.keys(material)).forEach((key) => {
      const values = material[key];
      const poGroup = groupBy(values, 'ponumber');

      const children: TreeNodeData[] = [];
      Object.keys(poGroup).forEach((k) => {
        const vals = poGroup[k].map((i) => ({
          ...i,
          remainingPoBalance: Number(i.remainingPoBalance),
          poAmount: Number(i.poAmount),
          unitcost: Number(i.unitcost),
          extdcost: Number(i.extdcost),
        }));

        const poTotal = sumBy(vals, 'extdcost');
        const rTotal = vals[0].remainingPoBalance;
        totalCost = totalCost + poTotal;
        totalRemFinal = totalRemFinal + rTotal;

        const leafChildren: TreeNodeData[] = vals.map((val) => ({
          value: String(val.id),
          label: `${val.itemdesc.length > 0 ? val.itemdesc : '-'} ${
            val.poLineStatus.length > 0 ? `(${val.poLineStatus})` : ''
          }`,
          nodeProps: {
            poAmount: formatCurrency(val.extdcost),
            remaining:
              val.poLineStatus !== 'Closed'
                ? formatCurrency(val.remainingPoBalance)
                : '',
          },
        }));

        children.push({
          value: k,
          label: k,
          nodeProps: {
            poAmount: formatCurrency(poTotal),
            remaining: formatCurrency(rTotal),
          },
          children: leafChildren,
        });
      });

      nodes.push({
        value: key,
        label: key.length === 0 ? 'No Description' : key,
        children,
      });
    });

    if (totalCost) {
      nodes.push({
        value: 'total',
        label: 'Total',
        nodeProps: {
          isTotal: true,
          poAmount: formatCurrency(totalCost),
          remaining: formatCurrency(totalRemFinal),
        },
      });
    }

    return nodes;
  }, [material]);

  return (
    <>
      {Object.keys(material).length > 0 ? (
        <Box style={{ height: 400, overflow: 'auto' }}>
          <Grid mb="xs">
            <Grid.Col span={6}>
              <Text fw={700} size="sm">
                Description
              </Text>
            </Grid.Col>
            <Grid.Col span="auto" ta="right">
              <Text fw={700} size="sm">
                PO Amount
              </Text>
            </Grid.Col>
            <Grid.Col span="auto" ta="right">
              <Text fw={700} size="sm">
                Remaining
              </Text>
            </Grid.Col>
          </Grid>
          <Tree
            data={treeData}
            expandOnClick
            renderNode={({ node, expanded, hasChildren, elementProps }) => (
              <Box {...elementProps}>
                <Grid>
                  <Grid.Col span={6}>
                    <Box
                      style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      {hasChildren &&
                        (expanded ? (
                          <IconChevronDown size={16} />
                        ) : (
                          <IconChevronRight size={16} />
                        ))}
                      <Text
                        size="sm"
                        fw={(node.nodeProps as any)?.isTotal ? 700 : undefined}
                        style={{ textTransform: 'capitalize' }}
                      >
                        {node.label}
                      </Text>
                    </Box>
                  </Grid.Col>
                  <Grid.Col span="auto" ta="right">
                    <Text
                      size="sm"
                      fw={
                        hasChildren || (node.nodeProps as any)?.isTotal
                          ? 700
                          : undefined
                      }
                    >
                      {(node.nodeProps as any)?.poAmount || ''}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span="auto" ta="right">
                    <Text
                      size="sm"
                      fw={
                        hasChildren || (node.nodeProps as any)?.isTotal
                          ? 700
                          : undefined
                      }
                    >
                      {(node.nodeProps as any)?.remaining || ''}
                    </Text>
                  </Grid.Col>
                </Grid>
              </Box>
            )}
          />
        </Box>
      ) : (
        <div style={{ height: 300 }}>
          <p>No items...</p>
        </div>
      )}
    </>
  );
};
